/**
 * Vue + Vite (SPA) 진입점.
 *
 * Nuxt 빌드(`npm run dev` / `npm run build`)는 이 파일을 쓰지 않는다.
 * Vite 빌드(`npm run vite:dev` / `npm run vite:build`)에서만 부트스트랩으로 사용되며,
 * Nuxt가 자동으로 해주던 일(plugins 실행, global middleware 등록, pinia/head 설치)을
 * 같은 순서로 수동 재현한다. pages/ components/ 등 원본 소스는 전혀 건드리지 않는다.
 */
import { createApp } from "vue";
import { createPinia } from "pinia";
import { createRouter, createWebHistory } from "vue-router";
import { routes } from "vue-router/auto-routes";
import { createHead } from "@unhead/vue/client";

// nuxt.config.ts 의 css 배열과 동일한 순서
// (@nuxtjs/tailwindcss 가 tailwind css 를 맨 앞에 주입했었음)
import "./tailwind.css";
import "../assets/css/style.css";
import "@fortawesome/fontawesome-free/css/all.css";

import App from "../app.vue";
import { createNuxtApp, createMiddlewareGuard, createPageMetaGuard } from "./nuxt/index.js";
import { registerNuxtComponents, NuxtErrorPage } from "./nuxt/components.js";

// --- Nuxt global middleware (파일명 알파벳 순서) ---
import authMiddleware from "../middleware/auth.global.js";
import closeModalMiddleware from "../middleware/closeModal.global.js";

// --- Nuxt plugins (파일명 알파벳 순서) ---
import autoLogin from "../plugins/autoLogin.js";
import counselorList from "../plugins/counselorList.js";
import counselorStatus from "../plugins/counselorStatus.js";
import deviceInfo from "../plugins/deviceInfo.js";
import globalClickHandler from "../plugins/globalClickHandler.client.js";

const app = createApp(App);

const pinia = createPinia();
app.use(pinia);

app.use(createHead());

const router = createRouter({
  history: createWebHistory("/view/"),
  routes: [
    ...routes,
    // Nuxt 는 매칭되는 라우트가 없으면 404 에러 페이지를 띄운다.
    // (nuxt/dist/pages/runtime/plugins/router.js — to.matched.length === 0 → showError)
    { path: "/:pathMatch(.*)*", component: NuxtErrorPage },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: "smooth" };
    return { top: 0 };
  },
});

registerNuxtComponents(app);

const nuxtApp = createNuxtApp({ vueApp: app, router, pinia });

const plugins = [autoLogin, counselorList, counselorStatus, deviceInfo, globalClickHandler];

/**
 * Nuxt 의 부팅 순서를 그대로 따른다.
 * (nuxt/dist/pages/runtime/plugins/router.js + nuxt/dist/app/entry.js)
 *
 *   1. vueApp.use(router)          → 초기 네비게이션 시작. 이 시점엔 미들웨어 가드가 없다.
 *   2. await router.isReady()
 *   3. 미들웨어 가드 등록
 *   4. 플러그인 전부 순차 await
 *   5. app:created → 초기 라우트를 force replace  ← 미들웨어가 처음 실행되는 지점
 *   6. mount
 *
 * 4 → 5 의 순서가 핵심이다. autoLogin 이 localStorage 토큰으로 auth 쿠키를 복원한
 * **뒤에** auth.global 미들웨어가 그 쿠키를 읽어야 하기 때문이다.
 * (예전 순서에서는 둘이 동시에 돌아서, 앱 자동로그인 사용자가 /login 에 그대로 머물렀다)
 */
(async () => {
  app.use(router);
  await router.isReady();

  // 글로벌 미들웨어(파일명 알파벳 순서) → 그 다음 페이지별 definePageMeta 미들웨어
  router.beforeEach(createMiddlewareGuard(authMiddleware));
  router.beforeEach(createMiddlewareGuard(closeModalMiddleware));
  router.beforeEach(createPageMetaGuard());

  for (const plugin of plugins) {
    try {
      await plugin(nuxtApp);
    } catch (e) {
      console.error("plugin 실행 실패:", e);
    }
  }

  // Nuxt 의 app:created 훅이 하는 일 — 초기 라우트를 다시 밟아 미들웨어를 실행한다.
  try {
    await router.replace({ ...router.currentRoute.value, name: undefined, force: true });
  } catch (e) {
    console.error("초기 라우팅 실패:", e);
  }

  app.mount("#app");
  await nuxtApp.callHook("app:mounted");
})();
